import { inject, Injectable, signal } from '@angular/core';
import { AppointmentSearchDto, EventsService } from '@app/client';
import { TableListData, TableRequestData } from '@app/core/components/table-list/table-list.interface';
import { DomainValueType } from '@app/core/enums/domain-values.enum';
import { DomainValuesSharedService } from '@app/core/services/domain-values.shared.service';
import { getNominative } from '@app/core/utils/contacts.utils';
import dayjs from 'dayjs';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { AppointmentFilters } from './interfaces/appointment-filters.interface';
import { AppointmentRow } from './interfaces/appointment-row.interface';
import { ChunkDownloadService } from '@app/core/services/chunk-download.service';
import { CsvUtilsService } from '@app/core/services/csv-utils.service';

export type AppointmentsListState = {
  data: TableListData<AppointmentRow>;
  requestData: TableRequestData<AppointmentFilters>;
};

@Injectable({
  providedIn: 'root',
})
export class AppointmentsListFacade {
  private eventsService = inject(EventsService);
  private domainValuesSharedService = inject(DomainValuesSharedService);
  private chunkDownloadService = inject(ChunkDownloadService);
  private csvUtilsService = inject(CsvUtilsService);

  state = signal<AppointmentsListState>({
    data: {
      list: [],
      total: 0,
    },
    requestData: {
      limit: 25,
      skip: 0,
      filters: {},
      sort: {
        field: 'createdAt',
        order: -1,
      },
      activeTab: 0,
    },
  });

  get getState() {
    return this.state();
  }

  getSortMapped() {
    const sortState = this.state().requestData.sort;

    const sortFilterMapping: Record<keyof AppointmentRow, string> = {
      id: '',
      createdAt: 'createdAt',
      nominative: 'nominative',
      phones: 'phones',
      emails: 'emails',
      address: 'address',
      start: 'start',
      end: 'end',
      type: 'type',
      agent: 'agent',
      state: 'state',
      insertUser: 'insertUser',
      source: 'source',
      projectSaleId: 'projectSaleId',
      projectPurchaseId: 'projectPurchaseId',
      isStar: 'isStar',
      openHouseParentEvent: 'openHouseParentEvent',
      doneStateDate: 'doneStateDate',
      opportunityInsertDate: 'opportunityInsertDate',
    };

    return sortState ? { [sortFilterMapping[sortState.field as keyof AppointmentRow]]: { order: sortState.order } } : undefined;
  }

  setIdsFilter(ids: string[]) {
    this.state.update((state) => {
      state.requestData.skip = 0;
      state.requestData.limit = 20;
      state.requestData.filters.ids = ids;
      return state;
    });
  }

  mapFilters() {
    const filters = this.getState.requestData.filters;
    const filterMapped: AppointmentSearchDto = {
      ids: filters.ids,
      nominative: filters.nominative,
      phone: filters.phone,
      email: filters.email,
      insertDateRange: filters.createdAt ? { start: filters.createdAt.start, end: filters.createdAt.end } : undefined,
      isStar: filters.isStar,
      userId: filters.agent,
      address: filters.address,
      eventStartRange: filters.start ? { start: filters.start.start, end: filters.start.end } : undefined,
      eventEndRange: filters.end ? { start: filters.end.start, end: filters.end.end } : undefined,
      eventType: filters.type,
      state: filters.state ? parseInt(filters.state) : undefined,
      insertUserId: filters.insertUser,
      sourceType: filters.source,
      states: filters.status ? [filters.status] : undefined,
      network: filters.network,
      hidePersonalEvents: filters.hidePersonalEvents,
    };

    return filterMapped;
  }

  search() {
    const filtersMapped = this.mapFilters();
    const sortMapped = this.getSortMapped();

    this.eventsService
      .search({
        limit: this.getState.requestData.limit,
        skip: this.getState.requestData.skip,
        form: filtersMapped,
        formOptions: sortMapped,
      } as any)
      .pipe(
        switchMap((res) => {
          const observables = res.data.map((d) => {
            const createdAt = (<any>d).createdAt;
            return forkJoin({
              id: of(d._id),
              nominative: of(getNominative(d.contactObj)),
              phones: of(d.contactObj?.phones?.map((p) => p.phoneNumber).join(' - ')),
              emails: of(d.contactObj?.emails?.map((p) => p.email).join(' - ')),
              createdAt: of(dayjs(createdAt).format('DD/MM/YYYY HH:mm')),
              isStar: of(d.isStar ? 'Si' : 'No'),
              address: of(d.address?.formatted_address),
              start: d.start ? of(dayjs(d.start as Date).format('DD/MM/YYYY HH:mm')) : of(''),
              end: d.end ? of(dayjs(d.end as Date).format('DD/MM/YYYY HH:mm')) : of(''),
              type: d.type ? this.domainValuesSharedService.getLabel(DomainValueType.EVENT_TYPE, d.type) : of(''),
              agent: of((d as any).userObj?.firstName + ' ' + (d as any).userObj?.lastName),
              state: d.state ? this.domainValuesSharedService.getLabel(DomainValueType.EVENT_STATE, d.state) : of('Non esitato'),
              insertUser: of(`${(d as any).insertUserObj?.firstName || ''} ${(d as any).insertUserObj?.lastName || ''}`),
              source: d.sourceType ? this.domainValuesSharedService.getLabel(DomainValueType.SOURCE_LEAD_TYPE, d.sourceType) : of(''),
              projectSaleId: of(d.projectSale),
              projectPurchaseId: of(d.projectPurchase),
            } as { [K in keyof Partial<AppointmentRow>]: Observable<Partial<AppointmentRow>[K]> } & Record<keyof AppointmentRow, Observable<any>>);
          });
          return observables.length > 0 ? forkJoin({ data: forkJoin(observables), total: of(res.total) }) : of({ data: [], total: 0 });
        }),
        tap((res) => {
          this.state.update((state) => {
            state.data.list = res.data;
            state.data.total = res.total;
            return state;
          });
        }),
      )
      .subscribe();
  }

  downloadCsv() {
    const filtersMapped = this.mapFilters();
    this.chunkDownloadService
      .downloadCSV(
        {
          list: this.eventsService.search.bind(this.eventsService) as any,
        },
        {
          skip: this.state().requestData.skip,
          limit: this.state().requestData.limit,
          /* formOptions: this.getSortMapped(), */
          form: filtersMapped,
        },
        this.csvUtilsService.mapEvents.bind(this.csvUtilsService),
      )
      .subscribe();
  }
}
